import React from 'react';
import { format } from 'date-fns';
import { isEmpty } from 'lodash';
import { EnvelopeIcon, EyeIcon, PencilSquareIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import { useToast } from '@dashboard/context/ToastContext';
import { fetcher } from '@dashboard/services/request';
import { submissionKey } from '@dashboard/lib/queryKeys';

import { getDataFieldTypeLabel } from '@dashboard/services/pageFieldTypes/dataFieldTypes';
import renderFieldValue from '@dashboard/services/pageFieldTypes/renderFieldValue';

import Card from '@dashboard/components/Card';
import CardContent from '@dashboard/components/CardContent';
import FieldEditDialog from '@dashboard/components/FieldEditDialog';
import Link from '@dashboard/components/Link';
import MultilineText from '@dashboard/components/MultilineText';
import PropertyList from '@dashboard/components/PropertyList';
import Spinner from '@dashboard/components/Spinner';
import { confirm } from '@dashboard/lib/confirm';

const SubmissionsDetail = ({ submission }) => {
  const { token, sellerId } = useAuth();
  const { emitToast } = useToast();
  const queryClient = useQueryClient();

  const handleSendSubmissionConfirmationEmail = async () => {
    const confirmed = await confirm('Send a new confirmation email to the customer?', {
      confirm: 'Send',
    });

    if (confirmed) {
      sendSubmissionConfirmationEmail();
    }
  };

  const {
    mutateAsync: sendSubmissionConfirmationEmail,
    isLoading: isSendingSubmissionConfirmationEmail,
  } = useMutation({
    mutationFn: async () => {
      return await fetcher(
        `/sellers/${sellerId}/submissions/${submission._id}/send-confirmation-email`,
        token,
        {
          method: 'POST',
        },
      );
    },
    onSuccess: () => {
      emitToast({
        title: 'Confirmation email resent',
      });
    },
    onError: () => {
      emitToast({
        title: 'Failed to send confirmation email',
        type: 'error',
      });
    },
  });

  const { mutateAsync: updateFields } = useMutation({
    mutationFn: data =>
      fetcher(`/sellers/${sellerId}/submissions/${submission._id}`, token, {
        method: 'PUT',
        body: JSON.stringify({ fields: data }),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: submissionKey(submission._id) });
      emitToast({
        title: 'Submission updated',
      });
    },
    onError: () => {
      emitToast({
        title: 'Failed to update submission fields',
        type: 'error',
      });
    },
  });

  if (!submission) {
    return null;
  }

  const submissionDetails = {
    'Form name': submission.page?.name ? (
      <Link to={`/pages/${submission.page?._id}`} className="font-medium">
        {submission.page?.name}
      </Link>
    ) : (
      '—'
    ),
    Created: format(new Date(submission.createdAt), 'd MMM, yyyy, HH:mm'),
  };

  const customerDetails = {
    'Customer name': (submission.customer && submission.customer.name) || 'Not provided',
    'Customer email':
      submission.customer && submission.customer.email ? (
        <Link to={`mailto:${submission.customer.email}`} target="_blank" variant="withIcon">
          <EnvelopeIcon className="h-4 w-4 mt-0.5" />
          {submission.customer.email}
        </Link>
      ) : (
        'Not provided'
      ),
  };

  const details = {
    'Customer ID': (submission.customer && `#${submission.customer._id}`) || '–',
    'Submission ID': `#${submission._id}`,
  };

  const queryParameters = submission.queryParameters
    ? Object.entries(submission.queryParameters).filter(([name]) => {
        return name !== 'previewKey' && name !== 'testing';
      })
    : [];

  return (
    <div className="flex flex-col lg:flex-row max-w-5xl items-start justify-center">
      <div className="order-2 lg:order-1 w-full lg:pr-8">
        <Card className="mx-auto">
          <CardContent>
            <PropertyList.Title>Submission</PropertyList.Title>
            {Object.entries(submissionDetails).map(([key, value], index) => (
              <PropertyList.Item key={index}>
                <PropertyList.ItemLabel>{key}</PropertyList.ItemLabel>
                <PropertyList.ItemValue>{value}</PropertyList.ItemValue>
              </PropertyList.Item>
            ))}
          </CardContent>
        </Card>

        <Card className="mx-auto">
          <CardContent>
            <PropertyList.Title>Customer</PropertyList.Title>
            {Object.entries(customerDetails).map(([key, value], index) => (
              <PropertyList.Item key={index}>
                <PropertyList.ItemLabel>{key}</PropertyList.ItemLabel>
                <PropertyList.ItemValue>{value}</PropertyList.ItemValue>
              </PropertyList.Item>
            ))}
          </CardContent>
        </Card>

        {submission.fields && submission.fields.length > 0 && (
          <Card className="mx-auto">
            <CardContent>
              <PropertyList.Title>
                Fields
                <div className="ml-auto">
                  <FieldEditDialog
                    fields={submission.fields}
                    page={submission.page}
                    updateFields={updateFields}
                  />
                </div>
              </PropertyList.Title>
              {submission.fields.map(field => (
                <PropertyList.Item key={field._id}>
                  <PropertyList.ItemLabel>
                    {field.label}
                    <div className="text-gray-500 text-xs font-normal">
                      {getDataFieldTypeLabel(field.type)}
                    </div>
                  </PropertyList.ItemLabel>
                  <PropertyList.ItemValue>
                    <MultilineText>{renderFieldValue(field)}</MultilineText>
                  </PropertyList.ItemValue>
                </PropertyList.Item>
              ))}
            </CardContent>
          </Card>
        )}

        <Card className="mx-auto">
          <CardContent>
            <PropertyList.Title>Details</PropertyList.Title>
            {Object.entries(details).map(([key, value], index) => (
              <PropertyList.Item key={index}>
                <PropertyList.ItemLabel>{key}</PropertyList.ItemLabel>
                <PropertyList.ItemValue>{value}</PropertyList.ItemValue>
              </PropertyList.Item>
            ))}
          </CardContent>
        </Card>

        {!isEmpty(queryParameters) && (
          <Card className="mx-auto">
            <CardContent>
              <PropertyList.Title>Query parameters</PropertyList.Title>
              {queryParameters.map(([name, value], index) => (
                <PropertyList.Item key={index}>
                  <PropertyList.ItemLabel>{name}</PropertyList.ItemLabel>
                  <PropertyList.ItemValue>{value}</PropertyList.ItemValue>
                </PropertyList.Item>
              ))}
            </CardContent>
          </Card>
        )}
      </div>

      <div className="lg:border-l lg:border-b-0 border-gray-100 lg:mt-4 lg:pl-8 ml-4 lg:ml-0 pb-4 flex flex-col sm:flex-row whitespace-nowrap lg:flex-col gap-x-4 gap-y-2 items-start order-1 lg:order-2 lg:w-[22rem]">
        {submission.customer && (
          <Link
            to={`/customers/${submission.customer._id}`}
            variant="withIcon"
            className="w-full gap-x-2"
          >
            <EyeIcon className="h-4 w-4" />
            View customer
          </Link>
        )}
        <FieldEditDialog
          fields={submission.fields}
          page={submission.page}
          updateFields={updateFields}
          trigger={
            <Link
              className={clsx(
                'w-full flex gap-x-2 items-center font-medium text-gray-900 cursor-pointer',
              )}
              onClick={() => {}}
            >
              <>
                <PencilSquareIcon className="w-4 h-4" />
                Edit fields
              </>
            </Link>
          }
        />
        <Link
          onClick={handleSendSubmissionConfirmationEmail}
          className={clsx(`w-full flex items-center gap-x-2`, {
            'pointer-events-none opacity-50 cursor-not-allowed':
              isSendingSubmissionConfirmationEmail,
          })}
        >
          <>
            <EnvelopeIcon className="h-4 w-4" />
            Resend confirmation
          </>
          {isSendingSubmissionConfirmationEmail && <Spinner className="h-4 w-4 ml-2" />}
        </Link>
      </div>
    </div>
  );
};

export default SubmissionsDetail;
